import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export type AccountType = "asset" | "liability" | "equity" | "revenue" | "expense";
export const ACCOUNT_TYPE: Record<AccountType, string> = {
  asset: "Ativo",
  liability: "Passivo",
  equity: "Patrimônio líquido",
  revenue: "Receita",
  expense: "Custo ou despesa",
};

export const SOURCE: StatusMap = {
  manual: { label: "manual", tone: "neutral" },
  rule: { label: "por evento", tone: "info" },
  reversal: { label: "estorno", tone: "warn" },
};

export const PERIOD_STATUS: StatusMap = {
  open: { label: "aberto", tone: "info" },
  closed: { label: "fechado", tone: "neutral" },
};

export const EVENT_TYPES: Array<{ value: string; label: string }> = [
  { value: "fiscal.invoice_issued", label: "NFS-e emitida" },
  { value: "fiscal.invoice_canceled", label: "NFS-e cancelada" },
  { value: "finance.payment_received", label: "Recebimento de cliente" },
  { value: "finance.payment_reversed", label: "Estorno de recebimento" },
  { value: "finance.payable_paid", label: "Pagamento de conta" },
  { value: "finance.receivable_created", label: "Conta a receber criada" },
  { value: "finance.payable_created", label: "Conta a pagar criada" },
];

export interface Account {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  parent_id: string | null;
  dre_group: string | null;
  postable: boolean;
  active: boolean;
}

export interface CostCenter {
  id: string;
  code: string;
  name: string;
  parent_id: string | null;
  origin_type: string | null;
  origin_id: string | null;
  active: boolean;
}

export interface DreGroup {
  code: string;
  label: string;
  sort: number;
  nature: "credit" | "debit";
}

export interface Entry {
  id: string;
  org_id: string;
  period_id: string;
  entry_date: string;
  memo: string;
  source: "manual" | "rule" | "reversal";
  source_event_type: string | null;
  rule_id: string | null;
  origin_type: string | null;
  origin_id: string | null;
  reverses_entry_id: string | null;
  total_cents: number;
  created_at: string;
}

export interface LedgerLine {
  line_id: string;
  entry_id: string;
  entry_date: string;
  line_no: number;
  account_code: string;
  account_name: string;
  account_type: AccountType;
  cost_center_code: string | null;
  debit_cents: number;
  credit_cents: number;
  line_memo: string | null;
}

export interface Period {
  id: string;
  year: number;
  month: number;
  status: "open" | "closed";
  closed_at: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
}

export interface RuleLine {
  account: string;
  side: "debit" | "credit";
  amount: string;
  cost_center?: string;
}

export interface RuleTemplate {
  memo?: string;
  date?: string;
  cost_center?: string;
  vars?: Record<string, number>;
  lines?: RuleLine[];
  reverse_of?: string;
  match_key?: string;
}

export interface PostingRule {
  id: string;
  event_type: string;
  name: string;
  template: RuleTemplate;
  active: boolean;
  updated_at: string;
}

export interface TrialRow {
  account_id: string;
  code: string;
  name: string;
  type: AccountType;
  dre_group: string | null;
  opening_cents: number;
  debit_cents: number;
  credit_cents: number;
  closing_cents: number;
}

export interface DreRow {
  line: string;
  label: string;
  sort: number;
  is_subtotal: boolean;
  amount_cents: number;
}

export interface MarginRow {
  cost_center_id: string | null;
  code: string;
  name: string;
  revenue_cents: number;
  taxes_cents: number;
  costs_cents: number;
  expenses_cents: number;
  margin_cents: number;
  margin_pct: number | null;
}

export interface EntryLineInput {
  account_id: string;
  cost_center_id?: string | null;
  debit_cents?: number;
  credit_cents?: number;
  memo?: string | null;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "accounting.install_defaults": { args: { p_org: U }; returns: number };
    "accounting.upsert_account": {
      args: { p_org: U; p_code: string; p_name: string; p_type: AccountType; p_dre_group?: string | null; p_postable?: boolean; p_active?: boolean };
      returns: U;
    };
    "accounting.upsert_cost_center": {
      args: { p_org: U; p_code: string; p_name: string; p_parent_code?: string | null; p_origin_type?: string | null; p_origin_id?: U | null; p_active?: boolean };
      returns: U;
    };
    "accounting.upsert_posting_rule": { args: { p_org: U; p_event_type: string; p_name: string; p_template: RuleTemplate; p_active?: boolean }; returns: U };
    "accounting.post_entry": { args: { p_org: U; p_date: string; p_memo: string; p_lines: EntryLineInput[]; p_idempotency_key?: string | null }; returns: U };
    "accounting.reverse_entry": { args: { p_entry: U; p_reason: string }; returns: U };
    "accounting.open_period": { args: { p_org: U; p_year: number; p_month: number }; returns: U };
    "accounting.close_period": { args: { p_org: U; p_year: number; p_month: number }; returns: Record<string, unknown> };
    "accounting.reopen_period": { args: { p_org: U; p_year: number; p_month: number; p_reason: string }; returns: null };
    "accounting.trial_balance": { args: { p_org: U; p_from: string; p_to: string }; returns: TrialRow[] };
    "accounting.dre": { args: { p_org: U; p_from: string; p_to: string }; returns: DreRow[] };
    "accounting.margin_by_cost_center": { args: { p_org: U; p_from: string; p_to: string }; returns: MarginRow[] };
    "accounting.valid_expression": { args: { p_expr: string }; returns: boolean };
    "accounting.eval_amount": { args: { p_expr: string; p_payload: Record<string, unknown>; p_vars?: Record<string, number> }; returns: number };
  }
}

export function useAccounts(orgId: string) {
  return useQuery({
    queryKey: ["accounting", "accounts", orgId],
    queryFn: () => unwrap<Account[]>(from("accounting", "chart_of_accounts").select("*").eq("org_id", orgId).order("code")),
  });
}

export function useCostCenters(orgId: string) {
  return useQuery({
    queryKey: ["accounting", "cost_centers", orgId],
    queryFn: () => unwrap<CostCenter[]>(from("accounting", "cost_centers").select("*").eq("org_id", orgId).order("code")),
  });
}

export function useDreGroups() {
  return useQuery({
    queryKey: ["accounting", "dre_groups"],
    queryFn: () => unwrap<DreGroup[]>(from("accounting", "dre_groups").select("*").order("sort")),
    staleTime: Infinity,
  });
}

export function usePeriods(orgId: string) {
  return useQuery({
    queryKey: ["accounting", "periods", orgId],
    queryFn: () => unwrap<Period[]>(from("accounting", "periods").select("*").eq("org_id", orgId).order("year", { ascending: false }).order("month", { ascending: false })),
  });
}

export const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
export function periodLabel(p: { year: number; month: number }): string {
  const m = MONTHS[p.month - 1] ?? "";
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} de ${p.year}`;
}

export function newIdempotencyKey(prefix: string): string {
  const rnd = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  return `${prefix}:${rnd}`;
}
